<template>
    <div class="replay-game-container" :class="{'is-mobile': isMobile}">
        <div class="loader" v-if="!loaded">
            <loader />
        </div>
        <div class="game-container-inner" v-if="loaded">
            <score-board
                :gameState="replayState"
                :replayControls="!isMobile"
                :currentMove="currentMove"
                :totalMoves="snapshots.length"
            />
            <div class="play-area" :class="{'four-player-game': numOfPlayers === 4}">

                <div class="play-section section-left">
                    <player-mat v-if="players.length > 2"
                        :gameState="replayState"
                        :player="playerInPosition('left')"
                        :showHand="true"
                        :unitsInBag="getPlayerUnits(playerInPosition('left'), 'in_bag')"
                        :unitsInHand="getPlayerUnits(playerInPosition('left'), 'in_hand')"
                        position="left"
                    />
                </div>

                <div class="section-middle">
                    <div
                        class="decrees-bar-wrapper"
                        :class="{expanded: decreesBarExpanded}"
                        v-if="decrees.length"
                    >
                        <decrees-bar :decrees="decrees" />
                        <div class="decrees-bar-label" @click="() => toggleDecreesBar()">
                            <span class="label-text">
                                Decrees
                            </span>
                            <fa-icon icon="angle-down" class="icon" />
                        </div>
                    </div>

                    <div class="play-section section-top">
                        <player-mat
                            :gameState="replayState"
                            :player="playerInPosition('top')"
                            :showHand="true"
                            :unitsInBag="getPlayerUnits(playerInPosition('top'), 'in_bag')"
                            :unitsInHand="getPlayerUnits(playerInPosition('top'), 'in_hand')"
                            position="top"
                            v-if="!isMobile"
                        />
                        <mobile-player-mat
                            :actionsLog="actionsLog"
                            :gameState="replayState"
                            :player="playerInPosition('top')"
                            :showHand="true"
                            :unitsInBag="getPlayerUnits(playerInPosition('top'), 'in_bag')"
                            :unitsInHand="getPlayerUnits(playerInPosition('top'), 'in_hand')"
                            position="top"
                            v-if="isMobile"
                        />
                    </div>

                    <div class="board-wrapper" ref="board-wrapper">
                        <board :gameState="replayState" />
                        <actions-log :actionsLog="actionsLog" v-if="!isMobile"/>
                    </div>

                    <div class="play-section section-bottom">
                        <player-mat
                            :gameState="replayState"
                            :player="playerInPosition('bottom')"
                            :showHand="true"
                            :unitsInBag="getPlayerUnits(playerInPosition('bottom'), 'in_bag')"
                            :unitsInHand="getPlayerUnits(playerInPosition('bottom'), 'in_hand')"
                            position="bottom"
                            v-if="!isMobile"
                        />
                        <mobile-player-mat
                            :gameState="replayState"
                            :player="playerInPosition('bottom')"
                            :showHand="true"
                            :unitsInBag="getPlayerUnits(playerInPosition('bottom'), 'in_bag')"
                            :unitsInHand="getPlayerUnits(playerInPosition('bottom'), 'in_hand')"
                            position="bottom"
                            v-if="isMobile"
                        />
                    </div>

                </div>

                <div class="play-section section-right">
                    <player-mat v-if="players.length > 2"
                        :gameState="replayState"
                        :player="playerInPosition('right')"
                        :showHand="true"
                        :unitsInBag="getPlayerUnits(playerInPosition('right'), 'in_bag')"
                        :unitsInHand="getPlayerUnits(playerInPosition('right'), 'in_hand')"
                        position="right"
                    />
                </div>
            </div>
            <actions-log-modal :lastLogIndex="snapshotIndex" />
            <victory-modal
                :show="gameHasEnded"
                :redirect="redirect"
                :gameState="replayState"
                :player="player"
            />
        </div>
        <div
            class="turn-notification animated"
            :class="[turnNotification.color, {
                'slide-in-right': turnNotification.slideIn,
                'slide-out-right': turnNotification.slideOut,
            }]"
            v-if="turnNotification.show"
        >
            <span class="turn-icon-wrapper">
                <royal-coin class-name="turn-icon" :color="turnNotification.color" />
            </span>
            <span class="notification-text">
                {{ turnNotification.text }}
            </span>
        </div>
        <div class="overlay"
            v-if="showShareModal"
            @click="onCloseShareModal"
        ></div>
        <share-modal
            :url="currentUrl"
            :currentMove="currentMove"
            :totalMoves="snapshots.length"
            v-if="showShareModal"
        />
        <div class="playback-instructions" v-if="showReplayInstructions">
            <div class="instructions-title" @click="setShowReplayInstructions(false)">
                <fa-icon icon="keyboard" class="icon instructions-title-icon" />
                <span class="instructions-title-label">Keyboard shortcuts</span>
                <fa-icon
                    icon="chevron-down"
                    class="icon instructions-minimise-icon"
                />
            </div>
            <div class="instructions-details">
                <div class="key-item">
                    <span class="key-label">
                        <span class="keyboard-key right-arrow">
                            <fa-icon icon="caret-down" class="icon" />
                        </span>
                    </span>
                    <span class="key-description">Next move</span>
                </div>
                <div class="key-item">
                    <span class="key-label">
                        <span class="keyboard-key left-arrow">
                            <fa-icon icon="caret-down" class="icon" />
                        </span>
                    </span>
                    <span class="key-description">Previous move</span>
                </div>
                <div class="key-item">
                    <span class="key-label">
                        <span class="keyboard-key up-arrow">
                            <fa-icon icon="caret-down" class="icon" />
                        </span>
                    </span>
                    <span class="key-description">First move</span>
                </div>
                <div class="key-item">
                    <span class="key-label">
                        <span class="keyboard-key down-arrow">
                            <fa-icon icon="caret-down" class="icon" />
                        </span>
                    </span>
                    <span class="key-description">Last move</span>
                </div>
                <div class="key-item">
                    <span class="key-label">
                        <span class="keyboard-key spacebar">
                            <span class="keyboard-key-text">
                                Spacebar
                            </span>
                        </span>
                    </span>
                    <span class="key-description">Play/pause</span>
                </div>
            </div>
        </div>
        <div
            class="playback-instructions minimised"
            v-if="!showReplayInstructions && !isMobile"
            @click="setShowReplayInstructions(true)"
        >
            <fa-icon icon="keyboard" class="icon instructions-title-icon" />
        </div>
        <div class="mobile-replay-controls" v-if="isMobile">
            <replay-controls />
        </div>
    </div>
</template>

<script>
import find from 'lodash.find';
import get from 'lodash.get';
import { MOBILE_MAX_WIDTH } from '../../App-types';
import { IGameStatus, IColor } from '../Game/Game-types';
import { isMobileDevice } from '../../helpers/general';

const { ENDED } = IGameStatus;

export default {
    name: 'Replay',
    async created() {
        const { redirect } = this.$route.params;

        const startAtMove = get(this.$route, 'query.move', null);

        try {
            if (redirect) {
                this.redirect = redirect.replace(/%2F/g, '/');
            }

            await this.$store.dispatch('setLoaded', false);
            await this.$store.dispatch('setPlayingState', false);
            await this.$store.dispatch('setSnapshotIndex', 0);
            await this.$store.dispatch('setSnapshots', []);
            await this.$store.dispatch('getSnapshots', this.gameId);
            await this.$store.dispatch('setReplayUnitTypes', []);
            await this.$store.dispatch('getReplayUnitTypes', this.gameId);
            await this.$store.dispatch('getSnapshotActionsLog', this.gameId);
            await this.$store.dispatch('getReplayDecrees', this.gameId);
            await this.$store.dispatch('getActionsLog', this.gameId);

            if (startAtMove && !isNaN(parseInt(startAtMove, 10))) {
                await this.$store.dispatch('setSnapshotIndex', parseInt(startAtMove, 10) - 1);
            }

            window.addEventListener('resize', this.onWindowResize);
        } catch (error) {

            this.$notify({
                group: 'app',
                title: 'Info',
                type: 'info',
                text: error.message,
            });

            if (redirect) {
                this.$router.push(redirect);
            } else {
                this.$router.push('/matches');
            }
        }
    },
    beforeUnmount() {
        this.$store.dispatch('setLoaded', false);
        this.$store.dispatch('setActionsLog', []);
        this.$store.dispatch('closeModal', 'actionsLog');
    },
    unmounted() {
        window.removeEventListener('resize', this.onWindowResize);
    },
    data() {
        return {
            decreesBarExpanded: false,
            turnNotification: {
                color: IColor.WHITE,
                show: false,
                slideIn: false,
                slideOut: false,
                text: '',
            },
            redirect: '/matches',
            victoryModalMinimized: false,
            windowWidth: window.innerWidth,
        };
    },
    watch: {
        async snapshotIndex(index, prevIndex) {
            if (index !== prevIndex && this.snapshots[index]) {
                await this.$store.dispatch('setReplayGameState', { snapshot: this.snapshots[index] });
            }
        },
    },
    computed: {
        actionsLog() {
            const logs = this.replayState.actionsLog;
            const currentSnapshot = this.snapshots[this.snapshotIndex];

            if (currentSnapshot) {
                return logs.filter(log => log.snapshotId < currentSnapshot.snapshotId);
            }

            return [];
        },
        activePlayer() {
            return this.players.filter(player => player.id === this.replayState.activePlayerId)[0];
        },
        currentMove() {
            return this.snapshotIndex + 1;
        },
        currentUrl() {
            return String(window.location.href).split('?move')[0];
        },
        decrees() {
            const currentSnapshotDecrees = [];

            for (const decree of this.replayState.decrees) {
                const snapshotDecree = this.snapshots[this.snapshotIndex]?.decrees.find(sd =>
                    sd.id === decree.id,
                );
                currentSnapshotDecrees.push({
                    ...decree,
                    blackTeamId: snapshotDecree?.blackTeamId,
                    whiteTeamId: snapshotDecree?.whiteTeamId,
                });
            }

            return currentSnapshotDecrees;
        },
        gameHasEnded() {
            return this.replayState.state === ENDED;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        isMobile() {
            return this.windowWidth <= (MOBILE_MAX_WIDTH + 50) || isMobileDevice();
        },
        loaded() {
            return this.replayState.loaded;
        },
        numOfPlayers() {
            return this.replayState.numOfPlayers;
        },
        players() {
            return this.replayState.players;
        },
        player() {
            return this.replayState.player;
        },
        replayState() {
            return this.$store.state.replayState;
        },
        snapshotIndex() {
            return this.replayState.snapshotIndex;
        },
        snapshots() {
            return this.replayState.snapshots;
        },
        showShareModal() {
            return this.$store.state.modalState.modals.share.show;
        },
        showReplayInstructions() {
            return this.$store.state.appState.showReplayInstructions;
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        winningPlayers() {
            return this.players.filter(player => player.teamId === this.replayState.winningTeamId);
        },
        winningTeam() {
            return find(this.replayState.teams, { id: this.replayState.winningTeamId });
        },
    },
    methods: {
        getPlayerUnits(player, state) {
            return player?.units.filter(unit => unit.state === state) ?? [];
        },
        isActivePlayer() {
            return this.activePlayer.id === this.player.id;
        },
        onCloseShareModal() {
            this.$store.dispatch('closeModal', 'share');
        },
        onToggleModal(type) {
            const dataKey = `${type}ModalMinimized`;
            this[dataKey] = !this[dataKey];
        },
        onWindowResize() {
            this.windowWidth = window.innerWidth;
        },
        playerInPosition(position) {
            const turnOrder = this.replayState.turnOrder;
            const numOfPlayers = this.replayState.numOfPlayers;
            let playerId = this.player.id;
            const index = turnOrder.indexOf(playerId);
            let offset;

            switch (position) {
                case 'top':
                    offset = numOfPlayers === 2 ? 1 : 2;
                    break;
                case 'right':
                    offset = 3;
                    break;
                case 'left':
                    offset = 1;
                    break;
                default:
                    offset = 0;
            }

            if (index + offset >= turnOrder.length) {
                playerId = turnOrder[index + offset - turnOrder.length];
            } else {
                playerId = turnOrder[index + offset];
            }

            return find(this.players, { id: playerId });
        },
        requestUndo() {
            this.$store.dispatch('requestUndo', this.gameId);
        },
        setShowReplayInstructions(value) {
            this.$store.dispatch('setShowReplayInstructions', value);
        },
        showTurnNotification(activePlayer) {
            const turnNotificationText = this.isActivePlayer()
                ? 'Your turn'
                : `${activePlayer.user.username}'s turn`;

            this.turnNotification.color = activePlayer.team.color;
            this.turnNotification.text = turnNotificationText;
            this.turnNotification.show = true;
            this.turnNotification.slideIn = true;

            setTimeout(() => {
                this.turnNotification.slideIn = false;
                this.turnNotification.slideOut = true;
            }, 2000);

            setTimeout(() => {
                this.turnNotification.show = false;
                this.turnNotification.slideOut = false;
            }, 2500);
        },
        toggleDecreesBar(value) {
            this.decreesBarExpanded = value ? value : !this.decreesBarExpanded;
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .replay-game-container {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        position: relative;
        text-align: center;

        .game-container-inner {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
        }

        .play-area {
            box-sizing: border-box;
            display: flex;
            height: 0;
            flex-grow: 1;
            justify-content: center;
            padding-top: 15px;
            position: relative;

            .play-section {
                margin: 0 auto;
            }

            .section-middle {
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                width: 100%;

                > * {
                    align-self: flex-start;
                    display: flex;
                    justify-content: center;
                    width: 100%;
                }

                .section-bottom,
                .section-top {
                    height: 20%;
                    max-height: 20%;

                    .player-mat {
                        align-self: center;
                        max-width: 75%;
                    }
                }

                .board-wrapper {
                    max-height: 60%;
                }

                .decrees-bar-wrapper {
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    left: 50%;
                    position: absolute;
                    top: 0;
                    transform: translateX(-50%) translateY(calc(-100% + 27px));
                    transition: transform 300ms cubic-bezier(0.2, 0, 0, 1) 0s;
                    width: 50%;
                    z-index: 10;

                    &.expanded {
                        transform: translate(-50%, 0);
                        width: 100%;

                        .icon {
                            transform: rotate(-180deg);
                        }

                        .decrees-bar {
                            padding: 8px 0;
                            visibility: visible;

                            .decrees-bar-content {
                                display: flex;

                                .decree-widget-wrapper {

                                    &:last-of-type {
                                        margin-right: 0;
                                    }
                                }
                            }
                        }

                        .decrees-bar-label {
                            background: white;
                            border-radius: 4px;
                        }
                    }

                    .decrees-bar-label {
                        align-self: center;
                        background: $light-beige;
                        border-top-width: 0;
                        border-bottom-left-radius: 4px;
                        border-bottom-right-radius: 4px;
                        box-shadow: 0px 1px 3px 1px rgba(123,125,125,0.25);
                        color: #2c3e50;
                        cursor: pointer;
                        font-size: .9em;
                        padding: 4px 10px;

                        .icon {
                            align-self: center;
                            margin-left: 5px;
                            transition: transform 300ms cubic-bezier(0.2, 0, 0, 1) 0s;
                            vertical-align: middle;
                        }
                    }

                    .decrees-bar {
                        visibility: hidden;
                    }
                }

                .board-wrapper {
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;
                    position: relative;

                    .game-board-wrapper {
                        margin: 0 auto;
                        order: 1;
                        width: 80%;
                    }

                    .actions-log-wrapper {
                        order: 0;
                    }
                }
            }

            .section-left,
            .section-right {
                align-self: center;
                max-width: 10%;
            }

            .section-left {
                padding-left: 10px;
            }

            .section-right {
                padding-right: 10px;
            }
        }

        .actions {
            margin: 10px auto 25px;

            ul {
                list-style-type: none;
                padding: 0;
                margin: 0;

                li {
                    display: inline-block;

                    &:not(:last-of-type) {
                        margin-right: 20px;
                    }
                }
            }
        }

        .loader {
            display: flex;
            height: 100vh;
            justify-content: center;
            align-items: center;
        }

        .score-board {
            padding: 5px 20px;
        }

        .chat {
            position: fixed;
            bottom: 25px;
            right: 25px;
        }

        .playback-instructions {
            background: white;
            border-radius: 5px;
            bottom: 25px;
            color: $dark-brown;
            padding: 10px 15px;
            position: fixed;
            right: 25px;
            text-align: left;
            z-index: 1001;

            &.minimised {
                background: white;
                border-radius: 50%;
                border: 1px solid #e5e2e6;
                box-sizing: border-box;
                cursor: pointer;
                height: 45px;
                width: 45px;

                .instructions-title-icon {
                    font-size: 1.2em;
                    left: 50%;
                    position: absolute;
                    top: 50%;
                    transform: translate(-50%, -50%);
                    vertical-align: middle;
                }
            }

            .instructions-title {
				cursor: pointer;
                font-size: .9em;
                font-weight: bold;
                margin-bottom: 10px;

                .instructions-title-icon {
                    margin-top: 3px;
                    vertical-align: middle;
                }

                .instructions-minimise-icon {
                    cursor: pointer;
                    margin-left: 12px;
                    margin-top: 3px;
                    vertical-align: middle;
                }

                .instructions-title-label {
                    margin-left: 5px;
                    vertical-align: middle;
                }
            }

            .instructions-details {

                .key-item {
                    padding: 5px 0;
                }

                .key-description {
                    font-size: .8em;
                    padding-left: 5px;
                    vertical-align: middle;
                }
            }
        }

        .keyboard-key {
            background: $key-background;
            border: 1px solid $key-border;
            border-bottom: 3px solid $key-border-dark;
            border-left: 3px solid $key-border-dark;
            border-radius: 5px;
            cursor: default;
            display: inline-flex;
            justify-content: center;
            min-height: 23px;
            min-width: 23px;
            vertical-align: middle;

            .icon {
                align-self: center;
                color: $key-icon;
                width: 8px;
            }

            &.spacebar {
                min-width: 80px;
            }

            &.left-arrow {

                .icon {
                    transform: rotate(90deg);
                }
            }

            &.right-arrow {

                .icon {
                    transform: rotate(-90deg);
                }
            }

            &.up-arrow {

                .icon {
                    transform: rotate(180deg);
                }
            }

            .keyboard-key-text {
                align-self: flex-end;
                color: $key-text;
                font-size: .6em;
                margin-bottom: 2.5px;
            }
        }

        .turn-notification {
            background: white;
            color: $dark-brown;
            font-family: 'RobotoSlab', 'Arial';
            font-size: 1.2em;
            padding: 17px 21px;
            position: fixed;
            right: 25px;
            text-align: center;
            top: 90px;
            vertical-align: middle;
            z-index: 5;
            white-space: nowrap;
            box-shadow: 0px 3px 3px 2px rgba(123,125,125,0.15);
            border-left: 5px solid $coin-border;

            &.black {
                color: $dark-gray;
                border-left: 5px solid $dark-gray;
            }

            .turn-icon-wrapper {
                display: inline-block;
                vertical-align: middle;
                margin-right: 5px;

                .turn-icon {
                    height: auto;
                    height: 30px;
                    width: 30px;
                    vertical-align: middle;
                    width: auto;
                }
            }

            .notification-text {
                display: inline-block;
                vertical-align: middle;
                text-transform: uppercase;
            }
        }

        .undo-wrapper {
            font-weight: bold;
            left: 25px;
            position: absolute;
            z-index: 3;

            .btn {
                &.btn-3d {
                    .undo-icon {
                        margin-right: 3px;
                    }

                    box-shadow: 0 3px $medium-gray;

                    &:hover {
                        box-shadow: 0 1px $medium-gray;
                    }

                    &.disabled {
                        opacity: .6;
                        cursor: not-allowed;

                        &:hover {
                            top: 0;
                            box-shadow: 0 3px $medium-gray;
                        }
                    }
                }
            }
        }

        .mobile-replay-controls {
            background: white;
            box-shadow: 0px -1px 2px 1px rgba(181, 181, 181, .35);
            box-sizing: border-box;
            color: $sage-brown;
            display: flex;
            padding: 6px 0;
            width: 100%;
            z-index: 10;
            justify-content: center;

            .replay-controls {

                .replay-control-slider {
                    display: none;
                }
            }
        }
    }

    @media only screen and (min-width: $screen-sm) {

        .replay-game-container {

            .chat {
                bottom: 3.5%;
                right: 2.5%;
            }

            .play-area {
                height: 100%;

                .section-middle {
                    justify-content: center;

                    .section-bottom,
                    .section-top {
                        height: 17%;
                        max-height: 17%;
                    }

                    .board-wrapper {
                        max-height: 65%;
                    }
                }
            }

        }
    }

    @media only screen and (min-width: $screen-md) {

        .replay-game-container {

            .playback-instructions {
                background: rgba(255,255,255,.75);
            }
        }

        .replay-game-container {

            .play-area {

                .section-middle {

                    .section-bottom,
                    .section-top {

                        .player-mat {
                            align-self: center;
                        }
                    }

                    .board-wrapper {

                        .game-board-wrapper {
                            width: 95%;
                        }
                    }
                }
            }

        }
    }

     @media only screen and (min-width: $screen-lg) {
        .replay-game-container {

            .play-area {

                .section-middle {

                    .section-bottom,
                    .section-top {

                        .player-mat {
                            max-width: 60%;
                        }
                    }

                    .board-wrapper {

                        .game-board-wrapper {
                            width: 100%;
                        }
                    }
                }
            }
        }
    }

</style>
